/* global console, setTimeout */

import {
  abortElaboration,
  checkboxEdit,
  checkboxInsert,
  coObjectAmountAIHDelete,
  coObjectAmountDelete,
  coObjectCheckBoxDelete,
  coObjectDimensionAttributeDelete,
  coObjectDropDownDelete,
  coObjectFreeTextDelete,
  coObjectTableDelete,
  coObjectTgkTextDelete,
  deleteComponentInstance,
  documentPartInsert,
  documentPartLink,
  documentPartNonTagetik,
  dropdownEdit,
  dropdownInsert,
  freeTextEdit,
  freeTextInsert,
  getCoObjectListToUpdate,
  lockCoObject,
  simpleEditComponentInstance,
  startElaborationForComponentInstanceInsert,
  startElaborationForComponentInstanceUpdate,
  startElaborationForTgkAmountAihInsert,
  startElaborationForTgkAmountAihUpdate,
  startElaborationForTgkAmountInsert,
  startElaborationForTgkAmountUpdate,
  startElaborationForTgkDimensionAttributeInsert,
  startElaborationForTgkDimensionAttributeUpdate,
  startElaborationForTgkTableInsert,
  startElaborationForTgkTableUpdate,
  startElaborationForTgkTextInsert,
  startElaborationForTgkTextUpdate,
  tgkAmountAihUpdateSimple,
  tgkAmountUpdateSimple,
  tgkDimensionAttributeUpdateSimple,
  tgkTableUpdateSimple,
  tgkTextUpdateSimple,
} from "../../../api/object/CoObjectGeneral";
import { addContentControlCustomXml, deleteXMLPart, editXMLPart } from "../../../utils/helpers/CustomXmlHelper";
import { insertCheckbox } from "../../../office/WordCheckbox";
import { insertDropDownList, updateDropDownList } from "../../../office/WordDropDownList";
import { insertFreeText } from "../../../office/WordFreeText";
import {
  CoObject,
  ElaborationAbortRequest,
  TgkAmountElaborationResponse,
  TgkDimensionAttributeElaborationResponse,
  TgkTableElaborationResponse,
  TgkTextElaborationResponse,
} from "../../../api/models";
import {
  createContentControlForCoObject,
  deleteContentControlById,
  lockObjectCannotEdit,
  updateInfo,
} from "../../../office/ContentControl";
import {
  CREATE_DOCUMENT_PART_NOTIFICATION,
  DELETE_OBJECT_NOTIFICATION,
  EDIT_OBJECT_NOTIFICATION,
  INSERT_OBJECT_NOTIFICATION,
} from "../../../utils/constants/notification";
import {
  CoObjectItem,
  DocumentPartSource,
  TgkAmountObjectSource,
  TgkTableObjectSource,
  TgkTextSource,
} from "../../../components/interfaces/CoObject";
import {
  DocumentPartRelativePosition,
  insertDocumentPart,
  moveCursorToContentControlId,
} from "../../../office/WordDocumentPart";
import { normalizeDocumentName } from "../../../utils/helpers/ObjectCheckerHelper";
import NotificationManager from "../../../common/notification/NotificationManager";
import { formatMessage, formatMessageDefault, formatMessageWithParams } from "../../../utils/helpers/LanguageHelper";
import {
  mapCheckboxConfigToCheckbox,
  mapCheckboxConfigToCheckboxItem,
  mapComponentInstanceElaborationRequest,
  mapComponentInstanceToXmlObject,
  mapDocumentPartConfigToDocumentPart,
  mapDocumentPartConfigToDocumentPartItem,
  mapDocumentPartIncludeToDocumentPartItem,
  mapDropDownConfigToDropDown,
  mapDropDownConfigToDropDownItem,
  mapFreeTextConfigToFreeText,
  mapFreeTextConfigToFreeTextItem,
  mapTgkAmountConfigToTgkAmount,
  mapTgkAmountConfigToTgkAmountItem,
  mapTgkDimensionAttributeConfigToTgkDimensionAttribute,
  mapTgkDimensionAttributeConfigToTgkDimensionAttributeItem,
  mapTgkTableConfigToTgkTable,
  mapTgkTableConfigToTgkTableItem,
  mapTgkTextConfigToTgkText,
  mapTgkTextConfigToTgkTextItem,
} from "./map-objects";
import { TableUpdateStrategy } from "../../../components/coobjects/co-panel/ObjectPanelComponent";
import { navigate } from "../general/navigation-slice";
import { EDIT_OBJECT } from "../../../utils/constants/navigation";
import { store } from "../../store/store";

interface InputType {
  coObject: CoObjectItem;
  userId: string;
  multilinked?: boolean;
  docPartIncludePosition?: DocumentPartRelativePosition;
  linkDetail?: { partDocumentId: string } | { documentDriveItemId: string };
  tableUpdateType?: TableUpdateStrategy["typeUpdate"];
}
export const fetchExecutedFormAndInsertAsync = async (input: InputType) => {
  let itemResult = null; // TODO should be CoObjectItem but there is an error on CoObjectSource model (CreateDocumentPartModal:93)
  try {
    if (input.coObject.info.componentReference !== undefined) {
      const ccId = await createContentControlForCoObject(input.coObject);
      input.coObject.contentControlId = `${ccId}`;
      await insertComponentInstance(input.coObject, input.userId);
    } else {
      const { userId, tableUpdateType, coObject } = input;
      const isDocPart = coObject.info.objectType === "DOCUMENT_PART_INCLUDE";
      const coObjectResult = await createCCByType(input);
      itemResult = isDocPart // "DOCUMENT_PART_INCLUDE" is the only one that create and insert elaboration in the same function (refinement todo)
        ? coObjectResult
        : await insertElaborationCCByType({ coObject: coObjectResult, userId, tableUpdateType });

      if (input.coObject.info.objectType != "DOCUMENT_PART_INCLUDE") {
        await moveCursorToContentControlId(Number(itemResult?.contentControlId));
        setTimeout(() => {
          store.dispatch(navigate(EDIT_OBJECT));
        }, 1000);
      }
    }

    if (
      input.coObject.info.objectType != "TABLE" &&
      input.coObject.info.objectType != "CHART" &&
      input.coObject.info.objectType != "AMOUNT" &&
      input.coObject.info.objectType != "AMOUNT_AIH" &&
      input.coObject.info.objectType != "DIMENSION_ATTRIBUTE" &&
      input.coObject.info.objectType != "TGK_TEXT" &&
      input.coObject.info.objectType != "DOCUMENT_PART_INCLUDE"
    ) {
      let message = formatMessage("cof.createnewobject.message.addsuccess");
      let title = formatMessage("cof.createnewobject.message.addsuccess.title");
      NotificationManager.addNotification({
        key: INSERT_OBJECT_NOTIFICATION,
        message: message,
        title: title,
        type: "success",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
    return itemResult;
  } catch (e) {
    console.warn(e);
    if (input.coObject.contentControlId != null && input.coObject.info.objectType === "DOCUMENT_PART_INCLUDE") {
      await deleteContentControlById(input.coObject.contentControlId);
    }
  }
};

export async function createCCByType({
  coObject,
  linkDetail,
  multilinked,
  docPartIncludePosition,
}: Omit<InputType, "tableUpdateType" | "userId">) {
  let ccId: number;
  switch (coObject.info.objectType) {
    case "TGK_TEXT":
    case "DIMENSION_ATTRIBUTE":
    case "AMOUNT":
    case "AMOUNT_AIH":
    case "TABLE":
    case "CHART": {
      ccId = await createContentControlForCoObject(coObject);
      break;
    }
    case "CHECKBOX": {
      ccId = await insertCheckbox(coObject);
      break;
    }
    case "DROPDOWN_LIST": {
      ccId = await insertDropDownList(coObject);
      break;
    }
    case "FREE_TEXT": {
      ccId = await insertFreeText(coObject);
      break;
    }
    case "DOCUMENT_PART_INCLUDE":
      return await createOrLinkDocPart(coObject, docPartIncludePosition, multilinked, linkDetail);
    default:
      return null;
  }

  coObject.contentControlId = ccId && ccId + "";
  return coObject;
}

export async function insertElaborationCCByType(
  input: Pick<InputType, "coObject" | "tableUpdateType" | "userId">
): Promise<CoObjectItem | null> {
  switch (input.coObject.info.objectType) {
    case "TABLE":
    case "CHART":
      return await insertTgkTable(input.coObject, input.userId, input.tableUpdateType);
    case "AMOUNT":
      return input.coObject.source.type === "AMOUNT_AIH"
        ? await insertTgkAIHAmount(input.coObject, input.userId)
        : await insertTgkAmount(input.coObject, input.userId);
    case "DIMENSION_ATTRIBUTE":
      return await insertTgkDimensionAttribute(input.coObject, input.userId);
    case "CHECKBOX":
      return await insertTgkCheckBox(input.coObject);
    case "DROPDOWN_LIST":
      await insertTgkDropDownList(input.coObject);
      return input.coObject;
    case "FREE_TEXT":
      return await insertTgkFreeText(input.coObject);
    case "TGK_TEXT":
      return await insertTgkText(input.coObject, input.userId);
  }
}

export const abortRefreshAsync = async (elaborationRequest: ElaborationAbortRequest) => {
  try {
    await abortElaboration(elaborationRequest);
  } catch (e) {
    NotificationManager.addNotification({
      key: EDIT_OBJECT_NOTIFICATION,
      message: formatMessageDefault("cof.elaboration.abort.message.error", "Error on elaboration abort: ") + e.message,
      title: formatMessageDefault(`cof.elaboration.abort.title`, "Elaboration abort"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
    });
    console.error(e.message, e);
  }
};

const handleEditComponentInstance = async (coObject: CoObjectItem) => {
  const response = await simpleEditComponentInstance({
    id: coObject.id,
    objectName: coObject.info.objectName,
    objectInfo: coObject.info.info,
  });
  coObject.id = response.id;
  await updateInfo(coObject, coObject.info.objectName);
  await editXMLPart(coObject.contentControlId, mapComponentInstanceToXmlObject(coObject));
};

const handleTable = (input: {
  coObject: CoObjectItem;
  userId: string;
  isChanged: boolean;
  tableUpdateType: TableUpdateStrategy["typeUpdate"];
}) => {
  if (input.isChanged)
    return startElaborationForTgkTableUpdate(
      mapTgkTableConfigToTgkTable(input.coObject, input.userId, input.tableUpdateType)
    );
  else return tgkTableUpdateSimple(mapTgkTableConfigToTgkTable(input.coObject, input.userId, input.tableUpdateType));
};

const handleAmount = (input: { coObject: CoObjectItem; userId: string; isChanged: boolean }) => {
  if (input.isChanged)
    return startElaborationForTgkAmountUpdate(mapTgkAmountConfigToTgkAmount(input.coObject, input.userId));
  else return tgkAmountUpdateSimple(mapTgkAmountConfigToTgkAmount(input.coObject, input.userId));
};

const handleAmountAih = (input: { coObject: CoObjectItem; userId: string; isChanged: boolean }) => {
  if (input.isChanged)
    return startElaborationForTgkAmountAihUpdate(mapTgkAmountConfigToTgkAmount(input.coObject, input.userId));
  else return tgkAmountAihUpdateSimple(mapTgkAmountConfigToTgkAmount(input.coObject, input.userId));
};

const handleTgkText = (input: { coObject: CoObjectItem; userId: string; isChanged: boolean }) => {
  if (input.isChanged) {
    return startElaborationForTgkTextUpdate(mapTgkTextConfigToTgkText(input.coObject, input.userId));
  }
  return tgkTextUpdateSimple(mapTgkTextConfigToTgkText(input.coObject, input.userId));
};

const handleDimensionAttribute = (input: { coObject: CoObjectItem; userId: string; isChanged: boolean }) => {
  if (input.isChanged)
    return startElaborationForTgkDimensionAttributeUpdate(
      mapTgkDimensionAttributeConfigToTgkDimensionAttribute(input.coObject, input.userId)
    );
  else
    return tgkDimensionAttributeUpdateSimple(
      mapTgkDimensionAttributeConfigToTgkDimensionAttribute(input.coObject, input.userId)
    );
};

export const fetchExecutedFormAndEditAsync = async (input: {
  coObject: CoObjectItem;
  userId: string;
  isChanged: boolean;
  tableUpdateType: TableUpdateStrategy["typeUpdate"];
}) => {
  try {
    let updatedObject: CoObject;
    let tgkTableElaborationResponse: TgkTableElaborationResponse;
    let tgkAmountElaborationResponse: TgkAmountElaborationResponse;
    let tgkDimensionAttributeElaborationResponse: TgkDimensionAttributeElaborationResponse;
    let tgkTextElaborationResponse: TgkTextElaborationResponse;
    let source;
    if (input.coObject.info.componentReference !== undefined) {
      await handleEditComponentInstance(input.coObject);
    } else {
      switch (input.coObject.info.objectType) {
        case "TABLE":
        case "CHART":
          tgkTableElaborationResponse = await handleTable(input);
          input.coObject.id = tgkTableElaborationResponse.id;
          source = input.coObject.source as TgkTableObjectSource;
          await updateInfo(
            input.coObject,
            source.dbApplication?.id +
              "-" +
              source.form.code +
              "-" +
              source.sheet.code +
              "-" +
              source.area?.name +
              "-" +
              source.chart?.name
          );
          editXMLPart(input.coObject.contentControlId, mapTgkTableConfigToTgkTableItem(input.coObject));
          break;
        case "AMOUNT":
          tgkAmountElaborationResponse =
            input.coObject.source.type === "AMOUNT_AIH" ? await handleAmountAih(input) : await handleAmount(input);
          input.coObject.id = tgkAmountElaborationResponse.id;
          input.coObject.source = {
            ...input.coObject.source,
            process: tgkAmountElaborationResponse.process,
          } as TgkAmountObjectSource;
          await updateInfo(input.coObject);
          editXMLPart(input.coObject.contentControlId, mapTgkAmountConfigToTgkAmountItem(input.coObject));
          break;
        case "DIMENSION_ATTRIBUTE":
          tgkDimensionAttributeElaborationResponse = await handleDimensionAttribute(input);
          input.coObject.id = tgkDimensionAttributeElaborationResponse.id;
          await updateInfo(input.coObject);
          editXMLPart(
            input.coObject.contentControlId,
            mapTgkDimensionAttributeConfigToTgkDimensionAttributeItem(input.coObject)
          );
          break;
        case "CHECKBOX":
          updatedObject = await checkboxEdit(mapCheckboxConfigToCheckbox(input.coObject));
          input.coObject.id = updatedObject.id;
          await updateInfo(input.coObject);
          editXMLPart(input.coObject.contentControlId, mapCheckboxConfigToCheckboxItem(input.coObject));
          break;
        case "DROPDOWN_LIST":
          // eslint-disable-next-line no-case-declarations
          let contentControlId = input.coObject.contentControlId;
          updatedObject = await dropdownEdit(mapDropDownConfigToDropDown(input.coObject));
          input.coObject.id = updatedObject.id;
          await updateInfo(input.coObject);
          if (input.isChanged) {
            contentControlId = await updateDropDownList(input.coObject);
          }
          editXMLPart(contentControlId, mapDropDownConfigToDropDownItem(input.coObject));
          break;
        case "FREE_TEXT":
          updatedObject = await freeTextEdit(mapFreeTextConfigToFreeText(input.coObject));
          input.coObject.id = updatedObject.id;
          await updateInfo(input.coObject);
          editXMLPart(input.coObject.contentControlId, mapFreeTextConfigToFreeTextItem(input.coObject));
          break;
        case "TGK_TEXT":
          tgkTextElaborationResponse = await handleTgkText(input);
          input.coObject.id = tgkTextElaborationResponse.id;
          input.coObject.source = {
            ...input.coObject.source,
            process: tgkTextElaborationResponse.process,
          } as TgkTextSource;
          await updateInfo(input.coObject);
          editXMLPart(input.coObject.contentControlId, mapTgkTextConfigToTgkTextItem(input.coObject));
          break;
      }
    }
    if (
      (input.coObject.info.objectType != "TABLE" &&
        input.coObject.info.objectType != "CHART" &&
        input.coObject.info.objectType != "AMOUNT" &&
        input.coObject.info.objectType != "AMOUNT_AIH" &&
        input.coObject.info.objectType != "DIMENSION_ATTRIBUTE" &&
        input.coObject.info.objectType != "TGK_TEXT") ||
      !input.isChanged
    ) {
      NotificationManager.addNotification({
        key: EDIT_OBJECT_NOTIFICATION,
        message: formatMessage("cof.createnewobject.message.editsuccess"),
        title: formatMessage("cof.createnewobject.message.editsuccess.title"),
        type: "success",
        closable: true,
        removeOnClose: true,
        isTemporary: true,
      });
    }
  } catch (e) {
    console.error(e.message, e);
    NotificationManager.addNotification({
      key: EDIT_OBJECT_NOTIFICATION,
      message: formatMessage("cof.createnewobject.message.editfailed") + e.message,
      title: formatMessage("cof.createnewobject.message.editfailed.title"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
      enableReloadPanel: e.enableRefreshPanelOption,
    });
  }
  return input.coObject.id;
};

export const deleteObjectAsync = async (input: { coObject: CoObjectItem }) => {
  try {
    if (input.coObject.info.componentReference !== undefined) {
      await deleteComponentInstance(input.coObject.id);
      await deleteContentControlById(input.coObject.contentControlId);
      deleteXMLPart(input.coObject.contentControlId);
    } else {
      switch (input.coObject.info.objectType) {
        case "AMOUNT":
          input.coObject.source.type === "AMOUNT_AIH"
            ? await coObjectAmountAIHDelete(input.coObject.id)
            : await coObjectAmountDelete(input.coObject.id);
          await deleteContentControlById(input.coObject.contentControlId);
          deleteXMLPart(input.coObject.contentControlId);
          break;
        case "DIMENSION_ATTRIBUTE":
          await coObjectDimensionAttributeDelete(input.coObject.id);
          await deleteContentControlById(input.coObject.contentControlId);
          deleteXMLPart(input.coObject.contentControlId);
          break;
        case "TABLE":
        case "CHART":
          await coObjectTableDelete(input.coObject.id);
          await deleteContentControlById(input.coObject.contentControlId);
          deleteXMLPart(input.coObject.contentControlId);
          break;
        case "CHECKBOX":
          await coObjectCheckBoxDelete(input.coObject.id);
          await deleteContentControlById(input.coObject.contentControlId);
          deleteXMLPart(input.coObject.contentControlId);
          break;
        case "DROPDOWN_LIST":
          await coObjectDropDownDelete(input.coObject.id);
          await deleteContentControlById(input.coObject.contentControlId);
          deleteXMLPart(input.coObject.contentControlId);
          break;
        case "FREE_TEXT":
          await coObjectFreeTextDelete(input.coObject.id);
          await deleteContentControlById(input.coObject.contentControlId);
          deleteXMLPart(input.coObject.contentControlId);
          break;
        case "TGK_TEXT":
          await coObjectTgkTextDelete(input.coObject.id);
          await deleteContentControlById(input.coObject.contentControlId);
          deleteXMLPart(input.coObject.contentControlId);
          break;
      }
    }
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessageWithParams("cof.createnewobject.message.deletesuccess", {
        coObjectName: input.coObject.info.objectName,
      }),
      title: formatMessage("cof.createnewobject.message.deletesuccess.title"),
      type: "success",
      closable: true,
      removeOnClose: true,
      isTemporary: true,
    });
  } catch (e) {
    NotificationManager.addNotification({
      key: DELETE_OBJECT_NOTIFICATION,
      message: formatMessageWithParams("cof.elaboration.deleteobject.message.notdelete", {
        coObjectName: input.coObject.info.objectName,
        exception: e.message,
      }),
      title: formatMessage("cof.createnewobject.message.deletefailed.title"),
      type: "error",
      closable: true,
      removeOnClose: true,
      isTemporary: false,
      enableReloadPanel: e.enableRefreshPanelOption,
    });
    return Promise.reject(e);
  }
};

export async function lockObject(coObject: CoObjectItem, lock: boolean) {
  let updatedCoObject: CoObject;
  updatedCoObject = await lockCoObject(coObject.id, lock);
  coObject = { ...coObject, id: updatedCoObject.id, isLocked: updatedCoObject.isLocked };
  await lockObjectCannotEdit(coObject.contentControlId, lock);
  switch (coObject.info.objectType) {
    case "AMOUNT":
    case "AMOUNT_AIH":
      await editXMLPart(
        coObject.contentControlId,
        coObject.info.componentReference
          ? mapComponentInstanceToXmlObject(coObject)
          : mapTgkAmountConfigToTgkAmountItem(coObject)
      );
      break;
    case "DIMENSION_ATTRIBUTE":
      await editXMLPart(
        coObject.contentControlId,
        coObject.info.componentReference
          ? mapComponentInstanceToXmlObject(coObject)
          : mapTgkDimensionAttributeConfigToTgkDimensionAttributeItem(coObject)
      );
      break;
    case "TABLE":
      await editXMLPart(
        coObject.contentControlId,
        coObject.info.componentReference
          ? mapComponentInstanceToXmlObject(coObject)
          : mapTgkTableConfigToTgkTableItem(coObject)
      );
      break;
    case "CHART":
      await editXMLPart(
        coObject.contentControlId,
        coObject.info.componentReference
          ? mapComponentInstanceToXmlObject(coObject)
          : mapTgkTableConfigToTgkTableItem(coObject)
      );
      break;
    case "CHECKBOX":
      await editXMLPart(coObject.contentControlId, mapCheckboxConfigToCheckboxItem(coObject));
      break;
    case "DROPDOWN_LIST":
      await editXMLPart(coObject.contentControlId, mapDropDownConfigToDropDownItem(coObject));
      break;
    case "FREE_TEXT":
      await editXMLPart(coObject.contentControlId, mapFreeTextConfigToFreeTextItem(coObject));
      break;
    case "TGK_TEXT":
      await editXMLPart(
        coObject.contentControlId,
        coObject.info.componentReference
          ? mapComponentInstanceToXmlObject(coObject)
          : mapTgkTextConfigToTgkTextItem(coObject)
      );
      break;
  }
}

export async function insertComponentInstance(coObject: CoObjectItem, userId: string) {
  const response = await startElaborationForComponentInstanceInsert(
    mapComponentInstanceElaborationRequest(coObject, userId)
  );
  coObject.id = response.id;
  await await addContentControlCustomXml(coObject.contentControlId, mapComponentInstanceToXmlObject(coObject));
  return coObject;
}

export async function updateComponentInstance(coObject: CoObjectItem, userId: string) {
  await startElaborationForComponentInstanceUpdate(mapComponentInstanceElaborationRequest(coObject, userId));
}

async function insertTgkTable(
  coObject: CoObjectItem,
  userId: string,
  tableUpdateType: TableUpdateStrategy["typeUpdate"]
) {
  let tgkTableElaborationResponse: TgkTableElaborationResponse;
  tgkTableElaborationResponse = await startElaborationForTgkTableInsert(
    mapTgkTableConfigToTgkTable(coObject, userId, tableUpdateType)
  );
  coObject.id = tgkTableElaborationResponse.id;
  await addContentControlCustomXml(coObject.contentControlId, mapTgkTableConfigToTgkTableItem(coObject));
  return coObject;
}

async function insertTgkAmount(coObject: CoObjectItem, userId: string) {
  const tgkAmountElaborationResponse = await startElaborationForTgkAmountInsert(
    mapTgkAmountConfigToTgkAmount(coObject, userId)
  );

  coObject.id = tgkAmountElaborationResponse.id;
  await addContentControlCustomXml(coObject.contentControlId, mapTgkAmountConfigToTgkAmountItem(coObject));
  return coObject;
}

async function insertTgkAIHAmount(coObject: CoObjectItem, userId: string) {
  const tgkAmountElaborationResponse = await startElaborationForTgkAmountAihInsert(
    mapTgkAmountConfigToTgkAmount(coObject, userId)
  );
  coObject.source = { ...coObject.source, process: tgkAmountElaborationResponse.process } as TgkTextSource;
  coObject.id = tgkAmountElaborationResponse.id;
  addContentControlCustomXml(coObject.contentControlId, mapTgkAmountConfigToTgkAmountItem(coObject));
  return coObject;
}

async function insertTgkDimensionAttribute(coObject: CoObjectItem, userId: string) {
  const tgkDimensionAttributeElaborationResponse = await startElaborationForTgkDimensionAttributeInsert(
    mapTgkDimensionAttributeConfigToTgkDimensionAttribute(coObject, userId)
  );

  coObject.id = tgkDimensionAttributeElaborationResponse.id;
  await addContentControlCustomXml(
    coObject.contentControlId,
    mapTgkDimensionAttributeConfigToTgkDimensionAttributeItem(coObject)
  );
  return coObject;
}

async function insertTgkCheckBox(coObject: CoObjectItem) {
  const tgkTableElaborationResponse = await checkboxInsert(mapCheckboxConfigToCheckbox(coObject));
  coObject.id = tgkTableElaborationResponse.id;
  await addContentControlCustomXml(coObject.contentControlId, mapCheckboxConfigToCheckboxItem(coObject));
  return coObject;
}

async function insertTgkDropDownList(coObject: CoObjectItem) {
  const tgkTableElaborationResponse = await dropdownInsert(mapDropDownConfigToDropDown(coObject));
  coObject.id = tgkTableElaborationResponse.id;
  await addContentControlCustomXml(coObject.contentControlId, mapDropDownConfigToDropDownItem(coObject));
}

async function insertTgkFreeText(input: CoObjectItem) {
  const tgkTableElaborationResponse = await freeTextInsert(mapFreeTextConfigToFreeText(input));
  input.id = tgkTableElaborationResponse.id;
  await addContentControlCustomXml(input.contentControlId, mapFreeTextConfigToFreeTextItem(input));
  return input;
}

async function insertTgkText(coObject: CoObjectItem, userId: string) {
  const tgkTextElaborationResponse = await startElaborationForTgkTextInsert(
    mapTgkTextConfigToTgkText(coObject, userId)
  );

  coObject.id = tgkTextElaborationResponse.id;
  coObject.source = { ...coObject.source, process: tgkTextElaborationResponse.process } as TgkTextSource;
  await addContentControlCustomXml(coObject.contentControlId, mapTgkTextConfigToTgkTextItem(coObject));
  return coObject;
}

async function createOrLinkDocPart(
  coObject: CoObjectItem,
  position?: DocumentPartRelativePosition,
  multilinked?: boolean,
  linkDetail?: { partDocumentId: string } | { documentDriveItemId: string } | undefined
) {
  let source: DocumentPartSource = coObject.source as DocumentPartSource;
  let operation: "link" | "scratch" | "nontagetik" = "scratch";
  if ((linkDetail as any)?.documentDriveItemId) operation = "nontagetik";
  else if (source.includedDocumentPartId != undefined || source.includedDocumentPartId != null) operation = "link";
  let itemResult = await insertDocPart(
    coObject,
    operation,
    position,
    multilinked,
    (linkDetail as any)?.documentDriveItemId
  );
  let message =
    operation !== "scratch"
      ? formatMessage("cof.createnewdocument.message.linksuccess")
      : formatMessage("cof.createnewdocument.message.createsuccess");
  let title =
    operation !== "scratch"
      ? formatMessage("cof.createnewdocument.link")
      : formatMessage("cof.createnewdocument.createdocumentpart");
  NotificationManager.addNotification({
    key: CREATE_DOCUMENT_PART_NOTIFICATION,
    message: message,
    title: title,
    type: "success",
    closable: true,
    removeOnClose: true,
    isTemporary: true,
  });
  return itemResult;
}

async function insertDocPart(
  coObject: CoObjectItem,
  operation: "link" | "scratch" | "nontagetik",
  position?: DocumentPartRelativePosition,
  multilinked?: boolean,
  driveItemId?: string
) {
  let source: DocumentPartSource = coObject.source as DocumentPartSource;
  const objectName = normalizeDocumentName(coObject.info.objectName);

  coObject.contentControlId = await insertDocumentPart(objectName, position);
  if (operation === "link") {
    const coObjectDocumentPart = await documentPartLink(
      mapDocumentPartConfigToDocumentPart(coObject, position, multilinked)
    );
    coObject.id = coObjectDocumentPart.id;
    coObject.info.objectName = coObjectDocumentPart.name;
    coObject.info.info = coObjectDocumentPart.desc;
    await updateInfo(coObject);
  } else if (operation === "scratch") {
    const coObjectDocumentPart = await documentPartInsert(
      mapDocumentPartConfigToDocumentPart(coObject, position, multilinked)
    );
    coObject.id = coObjectDocumentPart.id;
    source.includedDocumentPartId = coObjectDocumentPart.includedDocumentPartId;
  } else {
    const coObjectDocumentPart = await documentPartNonTagetik(
      driveItemId,
      mapDocumentPartConfigToDocumentPart(coObject, position, multilinked)
    );
    coObject.id = coObjectDocumentPart.id;
    source.includedDocumentPartId = coObjectDocumentPart.includedDocumentPartId;
  }
  await addContentControlCustomXml(coObject.contentControlId, mapDocumentPartConfigToDocumentPartItem(coObject));
  let coObjects = await getCoObjectListToUpdate(coObject.documentId);
  for (const coObject of coObjects) {
    await editXMLPart(coObject.contentControlId, mapDocumentPartIncludeToDocumentPartItem(coObject));
  }
  return coObject;
}
